import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {BookingCreatorState} from 'booking-creator/rootReducer';
import {BookingMode} from 'booking-creator/reducers/display/types';
import {BookingMethods, BookingType, DiaryMobileVersion} from 'shared-types/index';
import {customerActions} from 'booking-creator/reducers/customer/slice';
import {validationActions} from 'booking-creator/reducers/validation/slice';
import {quickValidationActions,} from 'app/reducers/quickValidation/slice';
import {displayActions} from 'booking-creator/reducers/display/slice';
import {activeBookingsActions} from 'booking-creator/reducers/activeBookings/slice';
import {activeSectionsActions} from 'booking-creator/reducers/activeSections/slice';
import {activeLayoutsActions} from 'booking-creator/reducers/activeLayouts/slice';
import {bookingTimesActions} from 'booking-creator/reducers/bookingTimes/slice';
import {bookingOptionsUpsellActions} from 'booking-creator/reducers/bookingOptionsUpsell/slice';
import {paymentActions} from 'booking-creator/reducers/payment/slice';
import {AppDispatch} from 'appRoot/Store';
import {scrollPositionActions} from 'booking-creator/reducers/scrollPosition/slice';
import {recurringBookingActions} from 'booking-creator/reducers/recurringBooking/slice';
import {activeScheduleActions} from 'booking-creator/reducers/activeSchedule/slice';
import {communicationActions} from 'booking-creator/reducers/communication/slice';
import {standbyListActions} from 'booking-creator/reducers/standbyList/slice';
import {tablesActions} from 'booking-creator/reducers/tables/slice';
import {tagsActions} from 'booking-creator/reducers/tags/slice';
import {cancelConfirmActions} from 'booking-creator/reducers/cancelConfirm/slice';
import {DiaryRootState} from 'app/appRoot/rootReducer';

const NS = 'BCHooks';

export const getBookingModeAndMethod = (mode: BookingMode, method: BookingMethods, bookingType: BookingType) => {
  const isViewMode = mode === BookingMode.viewBooking;
  const isEditMode = mode === BookingMode.editBooking;
  const isNewMode = mode === BookingMode.newBooking;
  const isWalkin = method === BookingMethods.walkin;
  const isIdleMode = mode === BookingMode.idle;
  const isNewWalkin = isWalkin && isNewMode;
  const isWaitListType = bookingType === BookingType.WaitList;
  const isStandbyList = bookingType === BookingType.StandbyList;

  return {
    isViewMode,
    isEditMode,
    isNewMode,
    isWalkin,
    isNewWalkin,
    isIdleMode,
    isWaitListType,
    isStandbyList,
  };
}

export const useCheckBookingModeAndMethod = () => {
  const mode = useSelector((state: BookingCreatorState) => state.displayReducer.mode);
  const method = useSelector((state: BookingCreatorState) => state.displayReducer.activeBooking?.method);
  const bookingType = useSelector((state: BookingCreatorState) => state.displayReducer.activeBooking?.bookingType);

  if (!method || !bookingType) {
    // defaults while activeBooking is still getting populated
    return getBookingModeAndMethod(BookingMode.idle, BookingMethods.phone, BookingType.Booking);
  }

  const bookingModeAndMethod = useMemo(() => {
    return getBookingModeAndMethod(mode, method, bookingType);
  }, [mode, method, bookingType])

  return bookingModeAndMethod;
};

export const useStandby = () => {
  const isLoading = useSelector((state: BookingCreatorState) => state.standbyReducer.isLoading);
  const errorMsg = useSelector((state: BookingCreatorState) => state.standbyReducer.errorMsg);
  return {
    isLoading,
    errorMsg,
};
};

export const resetReducers = (dispatch: AppDispatch) => {
  dispatch(activeBookingsActions.reset());
  dispatch(activeLayoutsActions.reset());
  dispatch(activeScheduleActions.reset());
  dispatch(activeSectionsActions.reset());
  dispatch(bookingOptionsUpsellActions.reset());
  dispatch(bookingTimesActions.reset());
  dispatch(communicationActions.reset());
  dispatch(customerActions.reset());
  dispatch(displayActions.reset());
  dispatch(paymentActions.reset()); // Have to make sure that reset is called after the new booking slider is completed
  dispatch(recurringBookingActions.reset());
  dispatch(scrollPositionActions.setScrollItems([]));
  dispatch(standbyListActions.reset());
  dispatch(tablesActions.reset());
  dispatch(tagsActions.reset());
  dispatch(validationActions.reset());
  dispatch(cancelConfirmActions.reset());
  dispatch(quickValidationActions.clearValidationAndSetBothStatuses());

  console.log('All reducers reset');
};

export const usePlusVersion = () =>
  useSelector(
    (state: DiaryRootState) =>
      state.settingReducer?.mobileVersion === DiaryMobileVersion.PlusVersion
  );
